// src/admin/pages/import-cost/ImportCostDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  Stack,
  Alert,
  CircularProgress,
  Divider,
  Backdrop,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import {
  Cost,
  CostFormData,
  CostCategory,
  CostType,
  CostStatus,
  RecurrenceFrequency,
  PAYMENT_METHODS,
  COST_CATEGORY_LABELS,
  COST_TYPE_LABELS,
  RECURRENCE_FREQUENCY_LABELS,
} from './importCost';
import { useCostMutations } from '../../../hooks/useImportCosts';
import { useAuth } from '../../../contexts/AuthContext';

interface CostDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  cost?: Cost | null;
  onShowToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const toDateInputValue = (date?: Date | null): string => {
  if (!date) return '';
  const d = new Date(date);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().split('T')[0];
};

const EMPTY_FORM: CostFormData = {
  title: '',
  category: CostCategory.OTHER,
  costType: CostType.ONE_TIME,
  amount: '',
  incurredDate: toDateInputValue(new Date()),
  recurrenceFrequency: '',
  endDate: '',
  status: CostStatus.ACTIVE,
  vendor: '',
  paymentMethod: '',
  notes: '',
};

const CostDrawer: React.FC<CostDrawerProps> = ({
  open,
  onClose,
  onSuccess,
  cost,
  onShowToast,
}) => {
  const { currentUser } = useAuth();
  const { create, update } = useCostMutations();

  const loading = create.isPending || update.isPending;
  const [savingOverlay, setSavingOverlay] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<CostFormData>(EMPTY_FORM);

  useEffect(() => {
    if (cost) {
      setFormData({
        title: cost.title || '',
        category: cost.category,
        costType: cost.costType,
        amount: cost.amount ? String(cost.amount) : '',
        incurredDate: toDateInputValue(cost.incurredDate),
        recurrenceFrequency: cost.recurrenceFrequency || '',
        endDate: toDateInputValue(cost.endDate),
        status: cost.status,
        vendor: cost.vendor || '',
        paymentMethod: cost.paymentMethod || '',
        notes: cost.notes || '',
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setError('');
  }, [cost, open]);

  const handleChange = (field: keyof CostFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCostTypeChange = (value: CostType) => {
    setFormData(prev => ({
      ...prev,
      costType: value,
      recurrenceFrequency: value === CostType.RECURRING ? (prev.recurrenceFrequency || RecurrenceFrequency.MONTHLY) : '',
      endDate: value === CostType.ONE_TIME ? '' : prev.endDate,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      onShowToast('Title is required', 'error');
      return;
    }
    const amount = parseFloat(formData.amount);
    if (!formData.amount || isNaN(amount) || amount <= 0) {
      setError('Enter a valid amount greater than zero');
      onShowToast('Enter a valid amount greater than zero', 'error');
      return;
    }
    if (!formData.incurredDate) {
      setError(formData.costType === CostType.RECURRING ? 'Start date is required' : 'Date is required');
      onShowToast(formData.costType === CostType.RECURRING ? 'Start date is required' : 'Date is required', 'error');
      return;
    }
    if (formData.costType === CostType.RECURRING && !formData.recurrenceFrequency) {
      setError('Recurrence frequency is required for recurring costs');
      onShowToast('Recurrence frequency is required for recurring costs', 'error');
      return;
    }

    setError('');
    setSavingOverlay(true);

    try {
      if (cost?.id) {
        const result = await update.mutateAsync({ costId: cost.id, formData });
        if (result.success) {
          onShowToast('Cost updated successfully', 'success');
          onSuccess();
          handleClose();
        } else {
          const msg = result.message || 'Failed to update cost';
          setError(msg);
          onShowToast(msg, 'error');
        }
      } else {
        if (!currentUser) {
          onShowToast('You must be logged in to add a cost', 'error');
          setSavingOverlay(false);
          return;
        }
        const result = await create.mutateAsync({
          formData,
          userId: currentUser.uid,
          userName: currentUser.displayName || currentUser.email || 'Unknown',
        });
        if (result.success) {
          onShowToast('Cost added successfully', 'success');
          onSuccess();
          handleClose();
        } else {
          const msg = result.message || 'Failed to add cost';
          setError(msg);
          onShowToast(msg, 'error');
        }
      }
    } catch (err: any) {
      console.error('Error saving cost:', err);
      const errorMessage = err.message || 'Failed to save cost';
      setError(errorMessage);
      onShowToast(errorMessage, 'error');
    } finally {
      setSavingOverlay(false);
    }
  };

  const handleClose = () => {
    setFormData(EMPTY_FORM);
    setError('');
    onClose();
  };

  const isEditing = !!cost;
  const isRecurring = formData.costType === CostType.RECURRING;
  const overlayLabel = isEditing ? 'Updating Cost…' : 'Adding Cost…';

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

          {/* ── Drawer Header ── */}
          <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 44, height: 44, borderRadius: 1.5, bgcolor: 'primary.main',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}
                >
                  <PaymentsIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    {isEditing ? 'Edit Cost' : 'Add New Cost'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isEditing ? 'Update this cost entry' : 'Record an overhead, operating, or other business cost'}
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={handleClose} disabled={loading} edge="end">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* ── Scrollable body ── */}
          <Box
            sx={{
              flex: 1, overflow: 'auto', px: 3, py: 3, minHeight: 0,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(128,128,128,0.3)', borderRadius: '3px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(128,128,128,0.5)' },
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2.5}>

              {/* ── Section: Core Details ── */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>
                  Core Details
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth required label="Title" placeholder="e.g., Show Room Rent — July"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  disabled={loading} size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth required select label="Category"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value as CostCategory)}
                  disabled={loading} size="small"
                >
                  {Object.values(CostCategory).map((cat) => (
                    <MenuItem key={cat} value={cat}>{COST_CATEGORY_LABELS[cat]}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth required label="Amount" type="number"
                  inputProps={{ min: 0, step: '0.01' }}
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  disabled={loading} size="small"
                />
              </Grid>

              {/* ── Section: Frequency ── */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 0.5 }} />
                <Typography variant="caption" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>
                  Frequency
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth required select label="Cost Type"
                  value={formData.costType}
                  onChange={(e) => handleCostTypeChange(e.target.value as CostType)}
                  disabled={loading} size="small"
                >
                  {Object.values(CostType).map((type) => (
                    <MenuItem key={type} value={type}>{COST_TYPE_LABELS[type]}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {isRecurring && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth required select label="Recurs"
                    value={formData.recurrenceFrequency}
                    onChange={(e) => handleChange('recurrenceFrequency', e.target.value as RecurrenceFrequency)}
                    disabled={loading} size="small"
                  >
                    {Object.values(RecurrenceFrequency).map((freq) => (
                      <MenuItem key={freq} value={freq}>{RECURRENCE_FREQUENCY_LABELS[freq]}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth required label={isRecurring ? 'Start Date' : 'Date Incurred'} type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.incurredDate}
                  onChange={(e) => handleChange('incurredDate', e.target.value)}
                  disabled={loading} size="small"
                />
              </Grid>

              {isRecurring && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="End Date (optional)" type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formData.endDate}
                    onChange={(e) => handleChange('endDate', e.target.value)}
                    disabled={loading} size="small"
                    helperText="Leave blank if ongoing"
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={isRecurring ? 12 : 6}>
                <TextField
                  fullWidth required select label="Status"
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value as CostStatus)}
                  disabled={loading} size="small"
                >
                  <MenuItem value={CostStatus.ACTIVE}>Active</MenuItem>
                  <MenuItem value={CostStatus.PAUSED}>Paused</MenuItem>
                  <MenuItem value={CostStatus.ENDED}>Ended</MenuItem>
                </TextField>
              </Grid>

              {/* ── Section: Additional Details ── */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 0.5 }} />
                <Typography variant="caption" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>
                  Additional Details
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Vendor / Payee" placeholder="e.g., City Utilities Board"
                  value={formData.vendor}
                  onChange={(e) => handleChange('vendor', e.target.value)}
                  disabled={loading} size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth select label="Payment Method"
                  value={formData.paymentMethod}
                  onChange={(e) => handleChange('paymentMethod', e.target.value)}
                  disabled={loading} size="small"
                >
                  <MenuItem value="">Not set</MenuItem>
                  {PAYMENT_METHODS.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth label="Notes" multiline rows={3}
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  disabled={loading} size="small"
                />
              </Grid>

            </Grid>
          </Box>

          {/* ── Drawer Footer ── */}
          <Divider />
          <Box sx={{ px: 3, py: 2, display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexShrink: 0 }}>
            <Button onClick={handleClose} disabled={loading}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} /> : null}
            >
              {loading ? 'Saving...' : isEditing ? 'Update Cost' : 'Add Cost'}
            </Button>
          </Box>

        </Box>
      </Drawer>

      {/* ── Full-screen saving overlay (mirrors SupplierDialog) ── */}
      <Backdrop
        open={savingOverlay}
        sx={{ zIndex: (t) => t.zIndex.modal + 100, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper', borderRadius: 3, px: 5, py: 4,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5,
            minWidth: 280, boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              {overlayLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>
    </>
  );
};

export default CostDrawer;
